import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import {
  render,
  MARK_BOLD,
  MARK_LINK,
  NODE_PARAGRAPH,
  NODE_HR,
  NODE_BR,
  NODE_HEADING,
  StoryblokRichtext,
} from "storyblok-rich-text-react-renderer";
import DynamicComponent from "../../utils/DynamicComponent/DynamicComponent.component";
import "./RichText.css";
import CustomLink from "../CustomLink/CustomLink.atom";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

interface RichTextProps {
  content: StoryblokRichtext;
  isArticleRichTextConfigRequired?: boolean;
  className?: string;
  alignment?: "left" | "center" | "right";

  /** Is there a heading printed *above* this RichText instance in this block? */
  hasHeadingAbove?: boolean;

  /** If there *is* a heading above, which level is it (e.g., 2 for <h2>, 3 for <h3>)? */
  headingAboveLevel?: HeadingLevel;

  /** Keep the visual style (sizes) of the editor’s original choice even if we change the tag. */
  preserveVisualFromOriginal?: boolean;

  allowH1?: boolean; // default false
}

/** Visual sizing classes for the *original* editor-selected level. */
function getVisualClassesForOriginalLevel(
  originalLevel: HeadingLevel,
  isArticleRichTextConfigRequired: boolean,
): string {
  if (isArticleRichTextConfigRequired) {
    switch (originalLevel) {
      case 2:
        return "text-xl md:text-2xl";
      case 3:
        return "text-lg md:text-xl";
      case 4:
        return "text-xl";
      case 5:
        return "text-base md:text-lg";
      case 6:
        return "text-base";
      default:
        return "text-xl md:text-2xl";
    }
  } else {
    switch (originalLevel) {
      case 2:
        return "text-2xl md:text-4xl";
      case 3:
        return "text-2xl md:text-4xl";
      case 4:
        return "text-xl";
      case 5:
        return "text-lg md:text-xl";
      case 6:
        return "text-base md:text-lg";
      default:
        return "text-2xl md:text-4xl";
    }
  }
}

const RichText: React.FC<RichTextProps> = ({
  content,
  isArticleRichTextConfigRequired = false,
  className,
  alignment,
  hasHeadingAbove = true,
  headingAboveLevel = 2,
  preserveVisualFromOriginal = true,
  allowH1 = false, // default false
}) => {
  useEffect(() => {
    removeSiteDirectoryFromAnchorHrefs();
  }, []);

  // Tracks the last FINAL heading level emitted by THIS RichText instance
  const lastLevelRef = useRef<HeadingLevel | null>(null);
  // Cascading offset to preserve relative shape after the first clamp (e.g., H5→H4 => offset -1)
  const offsetRef = useRef<number>(0);

  const baseline: HeadingLevel = allowH1 ? 1 : 2; // never emit <h1> from this component

  const removeSiteDirectoryFromAnchorHrefs = (): void => {
    const richTextArticles: Array<Element> = Array.from(document.getElementsByClassName("rich-text"));
    richTextArticles.map((richTextArticle: Element) => {
      const anchorTags: Array<Element> = Array.from(richTextArticle.getElementsByTagName("a")).filter(
        (anchorTag: Element) => !(anchorTag as HTMLElement).className.includes("custom-link"),
      );
      anchorTags.forEach((anchorTag: any) => {
        anchorTag.href = anchorTag.href.replaceAll(`${process.env.GATSBY_DOMAIN_FOLDER}/`, "");
      });
    });
  };

  const article = classNames(`rich-text prose max-w-none marker:text-sherpa-white`);
  const headings = classNames(
    `prose-headings:font-semibold prose-headings:leading-tight`,
    {
      "prose-headings:text-Primary-900": isArticleRichTextConfigRequired,
      "prose-headings:text-Body": !isArticleRichTextConfigRequired,
    },
    className,
  );
  const h1 = classNames(
    `prose-h1:font-extrabold prose-h1:text-2xl prose-h1:md:text-3xl prose-h1:xl:text-4xl prose-h1:3xl:text-5xl`,
  );
  const h2 = classNames("", {
    "prose-h2:text-xl prose-h2:md:text-2xl": isArticleRichTextConfigRequired,
    "prose-h2:text-2xl prose-h2:md:text-4xl": !isArticleRichTextConfigRequired,
  });
  const h3 = classNames("", {
    "prose-h3:text-lg prose-h3:md:text-xl": isArticleRichTextConfigRequired,
    "prose-h3:text-2xl prose-h3:md:text-4xl": !isArticleRichTextConfigRequired,
  });
  const h4 = classNames(`prose-h4:font-semibold prose-h4:text-xl`);
  const p = classNames(`prose-p:my-0 prose-p:break-word prose-p:leading-6`);
  // `prose` sets an explicit color on <strong> (--tw-prose-bold), which overrides the
  // inherited/marked text colour and makes bold text render black. Inherit instead so
  // bold keeps the surrounding (or RTE-configured) colour.
  const bold = classNames(`prose-strong:font-bold prose-strong:text-inherit`);

  return (
    <article
      className={classNames(`${article} ${headings} ${h1} ${h2} ${h3} ${h4} ${p} ${bold} space-y-6 flex flex-col`, {
        "text-left items-start justify-start": alignment === "left",
        "text-center items-center justify-center": alignment === "center",
        "text-right items-end justify-end": alignment === "right",
      })}
    >
      {render(content, {
        markResolvers: {
          [MARK_BOLD]: (children: any) => <strong>{children}</strong>,
          [MARK_LINK]: (children: any, props: any) => {
            const { href } = props;
            return (
              <CustomLink url={href} className="text-Primary-900">
                {children}
              </CustomLink>
            );
          },
        },
        nodeResolvers: {
          [NODE_HEADING]: (children: any, props: any) => {
            const desired = Number(props?.level ?? 2) as HeadingLevel;
            const prevFinal = lastLevelRef.current;

            // Determine the maximum allowed level for the *first* heading in this RTE,
            // based on whether a heading exists above and what level it is.
            // - If there IS a heading above (level L): first heading may be at most L+1.
            // - If there ISN'T: allow starting at baseline (H2) only.
            const firstAllowedMax: HeadingLevel = hasHeadingAbove
              ? (Math.min((headingAboveLevel + 1) as HeadingLevel, 6) as HeadingLevel)
              : baseline;

            const allowedMax =
              prevFinal == null ? firstAllowedMax : (Math.min((prevFinal + 1) as HeadingLevel, 6) as HeadingLevel);

            // Apply current offset first to preserve relative shape after earlier clamp(s)
            let target = Math.max(baseline, Math.min(desired + offsetRef.current, 6));

            // If target exceeds allowedMax for the outline, clamp and adjust offset
            if (target > allowedMax) {
              target = allowedMax;
              offsetRef.current = target - desired;
            }
            // Guard against going below baseline
            if (target < baseline) {
              target = baseline;
              offsetRef.current = target - desired;
            }

            const finalLevel = target as HeadingLevel;

            if (process.env.NODE_ENV !== "production" && finalLevel !== desired) {
              // eslint-disable-next-line no-console
              console.warn(
                `[RichText] Normalized heading from H${desired} to H${finalLevel}` +
                  (prevFinal ? ` (prev H${prevFinal})` : " (first in this RTE)") +
                  ` | offset=${offsetRef.current}` +
                  (hasHeadingAbove ? ` [headingAbove=H${headingAboveLevel}]` : " [no heading above]"),
              );
            }

            lastLevelRef.current = finalLevel;

            const Tag = `h${finalLevel}` as unknown as keyof JSX.IntrinsicElements;

            const visualLevel = preserveVisualFromOriginal ? desired : finalLevel;
            const visualClasses = getVisualClassesForOriginalLevel(
              visualLevel as HeadingLevel,
              isArticleRichTextConfigRequired,
            );

            return (
              <Tag className={visualClasses} data-original-level={desired} data-final-level={finalLevel}>
                {children}
              </Tag>
            );
          },

          [NODE_PARAGRAPH]: (children: any) => <p>{children}</p>,
          [NODE_BR]: () => <br />,
          [NODE_HR]: () => <hr />,
        },
        defaultBlokResolver: (name, props: any) => (
          <DynamicComponent blok={{ ...props, component: name }} key={props.uid} />
        ),
      })}
    </article>
  );
};

RichText.defaultProps = {
  content: undefined,
  hasHeadingAbove: true,
  headingAboveLevel: 2,
  preserveVisualFromOriginal: true,
};

RichText.propTypes = {
  content: PropTypes.any.isRequired,
};

export default RichText;
